import React, { useContext, useRef, useState, useEffect } from 'react';

import { Context } from '@store/store';

import { useQuery, useMutation } from '@apollo/client';
import { 
  GET_ELIB_DOC, 
  GET_ELIB_DOC_UPDATE, 
  GET_ELIB_DOC_CREATE 
} from '@graphql/editor';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import * as quillToWord from 'quill-to-word';
import { saveAs } from 'file-saver';

import SaveIcon from '@icons/editor/save-icon';
import DownloadIcon from '@icons/editor/download-icon';
import CloseIcon from '@icons/close-icon';
import AiIcon from '@icons/editor/ai-icon';

import { 
  Wrapper, 
  StyledHeader, 
  StyledBodyLoad,
  StyledQuill,
  BtnBlock, 
  DownloadBtn, 
  SaveBtn, 
  CloseBtn, 
  AiBtn,
  TitleInput 
} from './editor-comment.styles';

import EditorContextMenu from './editor-context-menu'

const EditorNewDoc = ({ onChange }) => {
  const [state, dispatch] = useContext(Context);
  const timerRef = useRef(null);
  const quillRef = useRef();

  // кастомное контекстное меню
  const [getContextMenuState, setContextMenuState] = useState('CLOSED');
  const [getContextMenuAnchorPoint, setContextMenuAnchorPoint] = useState({ x: 0, y: 0 });
  const handleQuillContextMenu = (e) => {
    let target = e.target || e.srcElement;
    if (target.closest('.ql-editor')) {
      if (typeof document.hasFocus === 'function' && !document.hasFocus()) {
        return;
      }

      e.preventDefault();
      setContextMenuAnchorPoint({ x: e.clientX, y: e.clientY });
      setContextMenuState('OPENED_BY_RIGHTCLICK');
    }
  }
  const showAiDropdown = (e) => {
    setContextMenuAnchorPoint({ x: e.clientX, y: e.clientY });
    setContextMenuState('OPENED_BY_ICON');
  }

  const { loading, error, data, refetch } = useQuery(GET_ELIB_DOC, {
    variables: { id: state.selectDoc },
    fetchPolicy: "no-cache",
  });
  const [updateDoc, { data:dataUpdate, loading:loadingUpdate, error:errorUpdate }] = useMutation(GET_ELIB_DOC_UPDATE);
  
  const [autoSaveBtn, setAutoSaveBtn] = useState(true);

  const [getTitle, setGetTitle] = useState('Новый конспект');
  const [getContent, setGetContent] = useState({ deltas: data?.elibReaderDoc?.delta ? `${data?.elibReaderDoc?.delta}` : '', content: data?.elibReaderDoc?.content ? `${data?.elibReaderDoc?.content}` : '' });
  const [getSource, setGetSource] = useState([]);

  const handleChange = (event) => {
    onChange(event)
  }

  const update = () => {
    updateDoc({
      variables: {
        id: state.selectDoc,
        title: `${getTitle}`,
        content: `${getContent.content}`,
        delta: `${getContent.deltas}`
      }
    }).then((res) => {
      setAutoSaveBtn(true);
      dispatch({ type: 'SHOW_EDITOR_MODAL', payload: true, action: 'Конспект сохранен' });
    })
  };

  const autoSave = () => {
    updateDoc({
      variables: {
        id: state.selectDoc,
        title: `${getTitle}`,
        content: `${getContent.content}`,
        delta: `${getContent.deltas}`
      }
    }).then((res) => {
      setAutoSaveBtn(true);
    });
  };

  const saveTimer = () => {
    setAutoSaveBtn(false);
    timerRef.current = setTimeout(() => {
      autoSave();
    }, 8000);
  }

  useEffect(() => {
    saveTimer();
    return () => clearTimeout(timerRef.current); // очистка таймера
  }, [getTitle, getContent.content]);

  const download = async (params) => {
    const delta = JSON.parse(getContent.deltas);
    const quillToWordConfig = {
        exportAs: 'blob'
    };
    const docAsBlob = await quillToWord.generateWord(delta, quillToWordConfig);
    saveAs(docAsBlob, `${getTitle}.docx`)
    dispatch({ type: 'SHOW_EDITOR_MODAL', payload: true, action: 'Конспект скачан' });
  };

  useEffect(() => {
    setGetTitle(data?.elibReaderDoc?.title || 'Новый конспект');
    setGetContent({deltas: data?.elibReaderDoc?.delta, content: data?.elibReaderDoc?.content});
    refetch();
  }, [state.selectDoc, data]);

  useEffect(() => {
    let container = document.querySelectorAll(".ql-editor p");
    let arr = [];
    container.forEach((element) => {
      arr.push(element.dataset.id);
    });
    let arr_1 = Array.from(new Set(arr));
    arr_1 = arr_1.filter(item => item !== undefined);
    setGetSource(arr_1);
    handleChange(arr_1);
  }, [getContent.content, data]);

  const fontSizeArr = ['small','normal','medium','large'];

  const Size = ReactQuill.Quill.import('attributors/style/size');
  Size.whitelist = fontSizeArr;
  ReactQuill.Quill.register(Size, true);

  const Parchment = ReactQuill.Quill.import('parchment');
  const IDAttribute = new Parchment.Attributor.Attribute('data-id-attribute', 'data-id', {
    scope: Parchment.Scope.BLOCK,
  });

  ReactQuill.Quill.register(
    {
      'attributors/attribute/id': IDAttribute,
    },
    true
  );

  ReactQuill.Quill.register(
    {
      'formats/id': IDAttribute,
    },
    true
  );

  const Block = ReactQuill.Quill.import('blots/block');

  class BlockBlot extends Block {
    constructor(domNode) {
      super(domNode);
      domNode.removeAttribute('data-id');
      this.cache = {};
    }
  }

  ReactQuill.Quill.register(BlockBlot);

  const modules = {
    toolbar: [
      [{ 'header': [false, 1, 2, 3] }],
      [{ 'size': fontSizeArr }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }],
      [{ 'align': [] }],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link', 'clean'],
    ],
  };

  if (loading) 
    return (
    <div style={{ display: state.isEditorNewDocSidebarOpened ? 'block' : 'none' }}>
      <Wrapper>
        <StyledBodyLoad>
          <p>Загрузка</p>
        </StyledBodyLoad>
      </Wrapper>
    </div>
    );

  if (error) 
    return (
    <div style={{ display: state.isEditorNewDocSidebarOpened ? 'block' : 'none' }}>
      <Wrapper>
        <StyledBodyLoad>
          <p>Ошибка: {error.message}</p>
        </StyledBodyLoad>
      </Wrapper>
    </div>
    );

  return (
    <div style={{ display: state.isEditorNewDocSidebarOpened ? 'block' : 'none' }}>
      <Wrapper>
        <StyledHeader style={{marginBottom: '0'}}>
          <TitleInput title="Переименовать конспект" type="text" placeholder="" value={getTitle} onChange={(e) => {setGetTitle(e.target.value)}}/>
          <BtnBlock>
            {getContent.content !== '' &&
              <AiBtn title="ИИ-тексты" onClick={(e) => showAiDropdown(e)}>
                <AiIcon/>
              </AiBtn>
            }
            {getContent.content !== '' &&
              <DownloadBtn title="Скачать конспект" onClick={() => download()}>
                <DownloadIcon/>
              </DownloadBtn>
            }
            <SaveBtn title="Сохранить конспект" onClick={() => update()}>
              <SaveIcon color={autoSaveBtn} />
            </SaveBtn>
            <CloseBtn title="Закрыть конспект" data-ignore-click-outside="sidebar" onClick={() => {
              dispatch({ type: 'TOGGLE_EDITOR_NEW_DOC_MODAL' });
            }}>
              <CloseIcon/>
            </CloseBtn>
          </BtnBlock>
        </StyledHeader>
        <StyledQuill onContextMenu={(e) => handleQuillContextMenu(e)}>
          <ReactQuill ref={quillRef} theme="snow" style={{height: 'calc(100% - 42px)'}} modules={modules} theme="snow" value={getContent.content} onChange={(content, delta, source, editor) => setGetContent({deltas: JSON.stringify(editor.getContents()), content: content})} />
          <EditorContextMenu
            getContextMenuState={getContextMenuState}
            setContextMenuState={setContextMenuState}
            getContextMenuAnchorPoint={getContextMenuAnchorPoint}
            setContextMenuAnchorPoint={setContextMenuAnchorPoint}
            getContent={getContent}
            quillRef={quillRef}
          />
        </StyledQuill>
      </Wrapper>
    </div>
  );
};

export default EditorNewDoc;